// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { withStyles } from '@material-ui/core/styles'

// Utils
import { getImageURL } from 'utils/resizer'

// Actions
import connectActions from 'utils/connectActions'
import { patchProposalLazy } from 'actions/proposals'

// Selectors
import { connectGetters } from 'react-redux-getters'
import { makeGetInboxItemsCount } from 'selectors/inbox'

// Atoms
import CircularProgress from '@material-ui/core/CircularProgress'
import Fade from '@material-ui/core/Fade'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import { AppSavingActivator } from 'atoms/AppSaving'
import Card from 'atoms/Card'
import Menu from 'atoms/Menu'
import ImageInput from 'atoms/ImageInput'
import Link from 'atoms/Link'

// Icons
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import CheckIcon from '@material-ui/icons/Check'

// Styles
const styles = theme => ({
  root: {
    position: 'relative',
    width: '11rem',
    height: '11rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  disabled: {
    '& $image': {
      opacity: 0.1
    },
    '& $label': {
      opacity: 0.5
    }
  },
  selected: {
    '& $check': {
      opacity: 1,
      transform: 'scale(1)'
    }
  },
  inner: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    border: 0,
    outline: 0,
    cursor: 'pointer',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: 'hidden',
    background: theme.palette.secondary.main,
    flex: 1,
  },
  image: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    opacity: 0.65,
    transition: theme.transitions.create('opacity'),
  },
  label: {
    position: 'relative',
    textAlign: 'center',
    padding: theme.spacing.unit * 2,
    color: theme.palette.primary.contrastText,
    overflow: 'hidden',
    wordBreak: 'break-word',
    transition: theme.transitions.create('opacity'),
    whiteSpace: 'pre-wrap'
  },
  check: {
    position: 'absolute',
    right: -theme.spacing.unit * 1.5,
    top: -theme.spacing.unit * 1.5,
    padding: theme.spacing.unit / 2,
    opacity: 0,
    transform: 'scale(0)',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%',
    lineHeight: 0,
    transition: theme.transitions.create(['opacity', 'transform']),
    '& svg': {
      width: 16,
      height: 16,
    }
  },
  bar: {
    height: '2rem',
    display: 'flex',
    alignItems: 'center',
  },
  barLabel: {
    flex: 1,
    textAlign: 'left',
    padding: theme.spacing.unit * 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  imageInputButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  imageProgress: {
    position: 'absolute',
    top: theme.spacing.unit * 1.5,
    right: theme.spacing.unit * 1.5,
    color: theme.palette.secondary.contrastText,
  }
})

const mapActionsToProps = {
  patchProposalLazyAction: patchProposalLazy,
}

const makeMapGettersToProps = () => {
  const getInboxItemsCount = makeGetInboxItemsCount()
  return (state, ownProps) => ({
    inboxItemsCountGetter: getInboxItemsCount(state, { proposalId: ownProps.proposal.id }),
  })
}

export default
@connectGetters(makeMapGettersToProps)
@connectActions(mapActionsToProps)
@withStyles(styles, { withTheme: true })
class ProposalListItem extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    proposal: PropTypes.object.isRequired,
    patchProposalLazyAction: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    inboxItemsCountGetter: PropTypes.object.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onUnsplashSelect: PropTypes.func.isRequired,
  }

  constructor({ proposal: { image } }) {
    super()
    this.state = {
      image,
      imageReady: false,
      nextImageLoading: false,
      menuAnchorEl: undefined,
    }
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    const { proposal: { image } } = nextProps
    if (image !== prevState.image) {
      return { image, imageReady: false }
    }
    return null
  }

  patchProposal = async (nextProposalData = {}) => {
    const { patchProposalLazyAction, proposal } = this.props
    const proposalData = {
      ...nextProposalData,
      data: {
        ...proposal.data,
        ...nextProposalData.data
      }
    }
    await patchProposalLazyAction.run({
      params: [proposal.uuid, proposalData],
    })
  }

  handleItemClick = () => {
    const { proposal, onSelect } = this.props
    onSelect(proposal)
  }

  handleMenuClick = (ev) => {
    this.setState({ menuAnchorEl: ev.currentTarget })
  }

  handleMenuClose = () => {
    this.setState({ menuAnchorEl: undefined })
  }

  handleDeleteClick = () => {
    const { onDelete } = this.props
    this.setState({ menuAnchorEl: undefined }, onDelete)
  }

  handleToggleDisabled = () => {
    this.setState({ menuAnchorEl: undefined }, async () => {
      const { proposal } = this.props
      await this.patchProposal({
        data: { disabled: !proposal.data.disabled }
      })
    })
  }

  handleNextImageLoading = () => {
    this.setState({ nextImageLoading: true })
  }

  handleImageChange = async (image) => {
    await this.patchProposal({
      image,
      data: { unsplash: null },
    })
    this.setState({ nextImageLoading: false })
  }

  handleUnsplashClick = () => {
    const { proposal, onUnsplashSelect } = this.props
    onUnsplashSelect(proposal)
  }

  handleImageReady = () => {
    this.setState({ imageReady: true })
  }

  renderBarLabel() {
    const { classes, proposal, inboxItemsCountGetter } = this.props
    let label

    if (proposal.data.disabled || proposal.data.disabledInstantly) {
      label = 'Inactive'
    } else if (inboxItemsCountGetter.isSucceded) {
      const inboxItemsCount = inboxItemsCountGetter.data
      if (inboxItemsCount === 0) {
        label = 'No responses'
      } else {
        // Without wrapping content into a span, link doesnt work in iOS
        label = (
          <Link to={`/inbox/proposal/${proposal.id}`}>
            <span>
              {inboxItemsCount}
              {' '}
              {inboxItemsCount === 1 ? 'response' : 'responses'}
            </span>
          </Link>
        )
      }
    }

    return (
      <Typography variant="caption" color="textSecondary" className={classes.barLabel}>
        { label }
      </Typography>
    )
  }

  renderMenu() {
    const { proposal } = this.props
    const { menuAnchorEl } = this.state
    return (
      <Fragment>
        <Menu
          open={!!menuAnchorEl}
          anchorEl={menuAnchorEl}
          onClose={this.handleMenuClose}
        >
          { !proposal.data.disabledInstantly && (
            <MenuItem onClick={this.handleToggleDisabled}>
              <Typography>
                Make
                {' '}
                { proposal.data.disabled ? 'active' : 'inactive' }
              </Typography>
            </MenuItem>
          ) }
          <MenuItem onClick={this.handleDeleteClick}>
            <Typography color="error">
              Delete
            </Typography>
          </MenuItem>
        </Menu>
        <IconButton onClick={this.handleMenuClick}>
          <MoreHorizIcon />
        </IconButton>
      </Fragment>
    )
  }

  renderImage() {
    const { classes } = this.props
    const { image, imageReady, nextImageLoading } = this.state
    const imageURL = getImageURL(image, 180)
    return (
      <Fragment>
        <img
          alt=""
          src={imageURL}
          style={{ display: 'none' }}
          onLoad={this.handleImageReady}
        />
        { imageReady && !nextImageLoading && (
          <Fade in>
            <div>
              <div
                className={classes.image}
                style={{ backgroundImage: `url(${imageURL})` }}
              />
            </div>
          </Fade>
        ) }
      </Fragment>
    )
  }

  renderImageInput() {
    const { classes, theme } = this.props
    const { imageReady, nextImageLoading } = this.state
    const pending = !imageReady || nextImageLoading
    return (
      <Fragment>
        { pending && (
          <div className={classes.imageProgress}>
            <CircularProgress color="inherit" size={theme.spacing.unit * 3} />
          </div>
        ) }
        <Fade in={!pending} exit={false}>
          <div>
            <ImageInput
              onLoading={this.handleNextImageLoading}
              onChange={this.handleImageChange}
              className={classes.imageInputButton}
            >
              <MenuItem onClick={this.handleUnsplashClick}>
                Browse Unsplash
              </MenuItem>
            </ImageInput>
          </div>
        </Fade>
      </Fragment>
    )
  }

  render() {
    const { classes, proposal, patchProposalLazyAction, selected } = this.props
    const cx = classNames(classes.root, {
      [classes.disabled]: proposal.data.disabled || proposal.data.disabledInstantly,
      [classes.selected]: selected,
    })
    return (
      <Fragment>
        { patchProposalLazyAction.isPending && <AppSavingActivator /> }
        <Card className={cx}>
          <button
            type="button"
            tabIndex="-1"
            className={classes.inner}
            onClick={this.handleItemClick}
          >
            { this.renderImage() }
            <Typography className={classes.label}>
              { proposal.name }
            </Typography>
          </button>
          <div className={classes.bar}>
            { this.renderBarLabel() }
            { this.renderMenu() }
          </div>
          { this.renderImageInput() }
          <div className={classes.check}>
            <CheckIcon />
          </div>
        </Card>
      </Fragment>
    )
  }
}
